import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "types";
import { useTranslation } from "../../../i18n";
import { athenaColors } from "../../../theme/palette";
import {
  athenaDarkSectionSx,
  athenaDarkPrimaryButtonSx,
  athenaDarkSecondaryButtonSx,
} from "../../layout/AthenaDialogStyles";
import { fetchData, createData } from "../../../api/apiHelpers";
import api from "../../../api/api";

const SIGNER_TITLES = [
  "CEO",
  "CTO",
  "COO",
  "CFO",
  "Medical Director",
  "Department Head",
  "Principal Investigator",
  "Legal Representative",
  "Data Protection Officer",
  "IT Director",
  "General Manager",
  "Other",
];

const INSTITUTION_TYPES = [
  { value: "a hospital and healthcare institution", label: "Hospital / Healthcare Institution" },
  { value: "a Contract Research Organization (CRO)", label: "Contract Research Organization (CRO)" },
  { value: "a pharmaceutical company", label: "Pharmaceutical Company" },
  { value: "a clinical research institution", label: "Clinical Research Institution" },
  { value: "a biotechnology company", label: "Biotechnology Company" },
  { value: "an academic medical center", label: "Academic Medical Center" },
  { value: "a private medical practice", label: "Private Medical Practice" },
];

const darkFieldSx = {
  "& .MuiInputLabel-root": { color: alpha(athenaColors.periwinkle, 0.7) },
  "& .MuiInputLabel-root.Mui-focused": { color: athenaColors.periwinkle },
  "& .MuiOutlinedInput-root": {
    color: athenaColors.white,
    bgcolor: alpha(athenaColors.white, 0.08),
    borderRadius: 1,
    "& fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.3) },
    "&:hover fieldset": { borderColor: athenaColors.periwinkle },
    "&.Mui-focused fieldset": { borderColor: athenaColors.periwinkle },
  },
  "& .MuiInputBase-input::placeholder": { color: alpha(athenaColors.periwinkle, 0.5) },
};

type AgreementModule = {
  name: string;
  label: string;
  status: "required" | "recommended" | "optional";
  description: string;
};

type AgreementModulesResponse = {
  modules: AgreementModule[];
  presets: Record<string, string[]>;
};

type LegalDocument = {
  id: string;
  document_type: string;
  version: string;
  language: string;
  title: string;
};

export type WizardStepAgreementProps = {
  agreementId: string | null;
  onAgreementCreated: (id: string) => void;
  onSigned: (selectedModules: string[]) => void;
  onBack: () => void;
  orgName: string;
};

type SubPhase = "configure" | "review" | "sign";

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function WizardStepAgreement({
  agreementId,
  onAgreementCreated,
  onSigned,
  onBack,
  orgName,
}: WizardStepAgreementProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const currentUser = useSelector((s: RootState) => s.auth.user);
  const signerName = [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(" ") || currentUser?.email || "";
  const [signerTitle, setSignerTitle] = useState("Legal Representative");
  const [institutionAddress, setInstitutionAddress] = useState("");
  const [institutionNif, setInstitutionNif] = useState("");
  const [institutionDescription, setInstitutionDescription] = useState("a hospital and healthcare institution");
  const [institutionContactEmail, setInstitutionContactEmail] = useState(currentUser?.email || "");
  const [subPhase, setSubPhase] = useState<SubPhase>("configure");
  const [modulesData, setModulesData] = useState<AgreementModulesResponse | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [selectedModules, setSelectedModules] = useState<string[]>(["core", "module_b"]);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [currentAgreementId, setCurrentAgreementId] = useState<string | null>(agreementId);
  const [generating, setGenerating] = useState(false);
  const [signing, setSigning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [signTab, setSignTab] = useState(0);
  const [confirmRead, setConfirmRead] = useState(false);
  const [confirmAuthorized, setConfirmAuthorized] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  // OTP state
  const [typedSignature, setTypedSignature] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpError, setOtpError] = useState("");

  const effectiveAgreementId = currentAgreementId ?? agreementId;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [modResp, docsResp] = await Promise.all([
          fetchData<AgreementModulesResponse>("/billing/legal/agreement/modules/"),
          fetchData<LegalDocument[]>("/billing/legal/documents/"),
        ]);
        if (!mounted) return;
        setModulesData(modResp);
        const doc = docsResp?.find((d) => d.document_type === "data_processing") ?? docsResp?.[0];
        if (doc) setDocumentId(doc.id);
        setLoadingData(false);
      } catch {
        if (mounted) {
          setError(t("contracts_wizard.agreement.load_error", "Failed to load agreement data."));
          setLoadingData(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, [t]);

  const handleModuleToggle = useCallback((name: string, checked: boolean) => {
    if (name === "core") return;
    setSelectedModules((prev) =>
      checked ? [...prev, name] : prev.filter((m) => m !== name)
    );
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!documentId || !modulesData) return;
    setGenerating(true);
    setError(null);
    try {
      const createResp = await createData<
        { document_id: string; selected_modules?: string[] },
        { id: string }
      >("/billing/legal/institution/agreements/", {
        document_id: documentId,
        selected_modules: selectedModules.filter((m) => m !== "core"),
      });
      const id = createResp.id;
      setCurrentAgreementId(id);
      onAgreementCreated(id);

      // Direct api.post needed for PDF preview response handling
      const previewResp = await api.post(
        `/billing/legal/institution/agreements/${id}/preview/`,
        {
          placeholder_values: {
            signer_name: signerName,
            signer_title: signerTitle,
            institution_address: institutionAddress,
            institution_nif: institutionNif,
            institution_description: institutionDescription,
            institution_contact_email: institutionContactEmail,
          },
        },
        { responseType: "blob" }
      );
      const blob = previewResp.data as Blob;
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
      setSubPhase("review");
    } catch (e) {
      setError(e instanceof Error ? e.message : t("contracts_wizard.agreement.generate_error", "Failed to generate agreement."));
    } finally {
      setGenerating(false);
    }
  }, [documentId, modulesData, selectedModules, onAgreementCreated, signerName, signerTitle, institutionAddress, institutionNif, institutionDescription, institutionContactEmail, t]);

  const handleDownloadPdf = useCallback(() => {
    if (!blobUrl) return;
    fetch(blobUrl)
      .then((r) => r.blob())
      .then((blob) => downloadBlob(blob, `unified_agreement_${orgName.replace(/\s+/g, "_")}.pdf`));
  }, [blobUrl, orgName]);

  const signatureMatches = typedSignature.trim().toLowerCase() === signerName.trim().toLowerCase();

  const handleRequestOtp = useCallback(async () => {
    setSendingOtp(true);
    setOtpError("");
    try {
      const res = await api.post("/billing/legal/agreement/request-otp/");
      setOtpSent(true);
      setEmailHint(res.data.email_hint || "");
    } catch (e: any) {
      setOtpError(e?.response?.data?.detail || t("contracts_wizard.agreement.otp_send_error", "Failed to send code"));
    } finally {
      setSendingOtp(false);
    }
  }, [t]);

  const handleContinueToSign = useCallback(() => {
    setSubPhase("sign");
    setSignTab(0);
    setConfirmRead(false);
    setConfirmAuthorized(false);
    setUploadFile(null);
    setTypedSignature("");
    setOtpSent(false);
    setOtpCode("");
    setOtpError("");
  }, []);

  const handleSignDigitally = useCallback(async () => {
    if (!effectiveAgreementId || !signatureMatches || !confirmRead || !confirmAuthorized || !otpCode) return;
    setSigning(true);
    setError(null);
    try {
      await createData(
        "/billing/legal/institution/agreements/" + effectiveAgreementId + "/sign/",
        {
          otp_code: otpCode,
          confirm_read: confirmRead,
          confirm_authorized: confirmAuthorized,
          signer_name: signerName,
          signer_title: signerTitle,
          institution_address: institutionAddress,
          institution_nif: institutionNif,
          institution_description: institutionDescription,
          institution_contact_email: institutionContactEmail,
        }
      );
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
      onSigned(selectedModules);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.response?.data?.message;
      setError(detail || (e instanceof Error ? e.message : t("contracts_wizard.agreement.sign_error", "Failed to sign agreement.")));
    } finally {
      setSigning(false);
    }
  }, [effectiveAgreementId, signatureMatches, confirmRead, confirmAuthorized, otpCode, blobUrl, onSigned, selectedModules, t]);

  const handleUploadSigned = useCallback(async () => {
    if (!effectiveAgreementId || !uploadFile) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("signed_pdf", uploadFile);
      // Direct api.post needed for PDF upload response handling
      await api.post<{ id: string; signature_detected: boolean }>(
        `/billing/legal/institution/agreements/${effectiveAgreementId}/upload-signed/`,
        formData
      );
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
      onSigned(selectedModules);
    } catch (e: any) {
      const detail = e?.response?.data?.message || e?.response?.data?.detail;
      if (e?.response?.status === 422) {
        setError(detail || t("contracts_wizard.agreement.upload_no_sig",
          "No digital signature detected. Please sign the PDF with a qualified tool (e.g. AutoFirma, Adobe Sign) or use the built-in signing flow."));
      } else {
        setError(detail || (e instanceof Error ? e.message : t("contracts_wizard.agreement.upload_error", "Failed to upload signed PDF.")));
      }
    } finally {
      setUploading(false);
    }
  }, [effectiveAgreementId, uploadFile, blobUrl, onSigned, selectedModules, t]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setUploadFile(f ?? null);
    setError(null);
  }, []);

  if (subPhase === "configure") {
    return (
      <Stack spacing={2}>
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              bgcolor: alpha(theme.palette.error.main, 0.12),
              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
              "& .MuiAlert-icon": { color: theme.palette.error.light },
              "& .MuiAlert-message": { color: alpha(athenaColors.white, 0.95) },
            }}
          >
            {error}
          </Alert>
        )}
        {!loadingData && !documentId && (
          <Alert
            severity="warning"
            sx={{
              bgcolor: alpha(athenaColors.periwinkle, 0.12),
              border: `1px solid ${alpha(athenaColors.periwinkle, 0.3)}`,
              "& .MuiAlert-icon": { color: athenaColors.periwinkle },
              "& .MuiAlert-message": { color: athenaColors.periwinkle },
            }}
          >
            {t("contracts_wizard.agreement.no_document", "No agreement document found. Please contact support.")}
          </Alert>
        )}
        {modulesData && (
          <>
            <Box sx={athenaDarkSectionSx}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: athenaColors.white, mb: 0.5 }}>
                {t("contracts_wizard.agreement.modules_title", "Agreement scope")}
              </Typography>
              <Typography variant="caption" sx={{ color: alpha(athenaColors.white, 0.55), mb: 1.5, display: "block" }}>
                {t("contracts_wizard.agreement.modules_subtitle", "Choose which relationship you want to establish with Athena Tech. The core agreement is always included.")}
              </Typography>
              <Stack spacing={1.5}>
                {modulesData.modules.map((m) => {
                  const isCore = m.name === "core";
                  const isRecommended = m.status === "recommended";
                  const isChecked = selectedModules.includes(m.name);
                  return (
                    <Box
                      key={m.name}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: `1px solid ${alpha(
                          isChecked ? athenaColors.periwinkle : athenaColors.white,
                          isChecked ? 0.35 : 0.1
                        )}`,
                        bgcolor: alpha(athenaColors.periwinkle, isChecked ? 0.08 : 0),
                        transition: "all 0.2s",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isChecked}
                            disabled={isCore}
                            onChange={(_, c) => handleModuleToggle(m.name, c)}
                            sx={{
                              color: alpha(athenaColors.white, 0.6),
                              "&.Mui-checked": { color: athenaColors.periwinkle },
                              "&.Mui-disabled.Mui-checked": { color: alpha(athenaColors.periwinkle, 0.6) },
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                              <Typography variant="body2" sx={{ color: athenaColors.white, fontWeight: 700 }}>
                                {m.label}
                              </Typography>
                              {isCore && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    px: 0.75, py: 0.15,
                                    borderRadius: 0.5,
                                    bgcolor: alpha(athenaColors.white, 0.12),
                                    color: alpha(athenaColors.white, 0.7),
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.3,
                                  }}
                                >
                                  {t("contracts_wizard.agreement.always_included", "Always included")}
                                </Typography>
                              )}
                              {isRecommended && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    px: 0.75, py: 0.15,
                                    borderRadius: 0.5,
                                    bgcolor: alpha(theme.palette.success.main, 0.2),
                                    color: theme.palette.success.light,
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.3,
                                  }}
                                >
                                  {t("contracts_wizard.agreement.recommended", "Recommended")}
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ color: alpha(athenaColors.white, 0.7), mt: 0.5, display: "block", lineHeight: 1.5 }}>
                              {m.description}
                            </Typography>
                          </Box>
                        }
                        sx={{ alignItems: "flex-start", m: 0, width: "100%" }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            <Box sx={athenaDarkSectionSx}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: athenaColors.white, mb: 1.5 }}>
                {t("contracts_wizard.agreement.signer_info_title", "Signer information")}
              </Typography>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="caption" sx={{ color: alpha(athenaColors.white, 0.7), mb: 0.5, display: "block" }}>
                    {t("contracts_wizard.agreement.signer_name_label", "Name (from your account)")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: athenaColors.white, fontWeight: 600 }}>
                    {signerName || "-"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: alpha(athenaColors.white, 0.7), mb: 0.5, display: "block" }}>
                    {t("contracts_wizard.agreement.signer_title_label", "Your position / title")}
                  </Typography>
                  <Select
                    value={signerTitle}
                    onChange={(e) => setSignerTitle(e.target.value)}
                    displayEmpty
                    size="small"
                    fullWidth
                    sx={{
                      color: athenaColors.white,
                      bgcolor: alpha(athenaColors.white, 0.08),
                      borderRadius: 1,
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha(athenaColors.periwinkle, 0.3) },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: athenaColors.periwinkle },
                      "& .MuiSvgIcon-root": { color: athenaColors.periwinkle },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: athenaColors.deepNavy,
                          color: athenaColors.white,
                          "& .MuiMenuItem-root:hover": { bgcolor: alpha(athenaColors.periwinkle, 0.15) },
                          "& .Mui-selected": { bgcolor: alpha(athenaColors.periwinkle, 0.25) },
                        },
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>{t("contracts_wizard.agreement.select_title", "Select your position")}</em>
                    </MenuItem>
                    {SIGNER_TITLES.map((title) => (
                      <MenuItem key={title} value={title}>{title}</MenuItem>
                    ))}
                  </Select>
                </Box>
              </Stack>
            </Box>

            <Box sx={athenaDarkSectionSx}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: athenaColors.white, mb: 1.5 }}>
                {t("contracts_wizard.agreement.institution_details_title", "Institution details")}
              </Typography>
              <Typography variant="caption" sx={{ color: alpha(athenaColors.white, 0.6), mb: 1.5, display: "block" }}>
                {t("contracts_wizard.agreement.institution_details_subtitle", "These details will appear in the agreement. Please verify they are correct.")}
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  label={t("contracts_wizard.agreement.institution_address", "Registered address")}
                  value={institutionAddress}
                  onChange={(e) => setInstitutionAddress(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="e.g. Carrer de Valencia, 123, 08011 Barcelona, Spain"
                  sx={darkFieldSx}
                />
                <TextField
                  label={t("contracts_wizard.agreement.institution_nif", "Tax ID / NIF")}
                  value={institutionNif}
                  onChange={(e) => setInstitutionNif(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="e.g. B12345678"
                  sx={darkFieldSx}
                />
                <Box>
                  <Typography variant="caption" sx={{ color: alpha(athenaColors.white, 0.7), mb: 0.5, display: "block" }}>
                    {t("contracts_wizard.agreement.institution_type", "Type of institution")}
                  </Typography>
                  <Select
                    value={institutionDescription}
                    onChange={(e) => setInstitutionDescription(e.target.value)}
                    displayEmpty
                    size="small"
                    fullWidth
                    sx={{
                      color: athenaColors.white,
                      bgcolor: alpha(athenaColors.white, 0.08),
                      borderRadius: 1,
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha(athenaColors.periwinkle, 0.3) },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: athenaColors.periwinkle },
                      "& .MuiSvgIcon-root": { color: athenaColors.periwinkle },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: athenaColors.deepNavy,
                          color: athenaColors.white,
                          "& .MuiMenuItem-root:hover": { bgcolor: alpha(athenaColors.periwinkle, 0.15) },
                          "& .Mui-selected": { bgcolor: alpha(athenaColors.periwinkle, 0.25) },
                        },
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>{t("contracts_wizard.agreement.select_institution_type", "Select your institution type")}</em>
                    </MenuItem>
                    {INSTITUTION_TYPES.map((it) => (
                      <MenuItem key={it.value} value={it.value}>{it.label}</MenuItem>
                    ))}
                  </Select>
                </Box>
                <TextField
                  label={t("contracts_wizard.agreement.institution_email", "Contact email for legal notices")}
                  value={institutionContactEmail}
                  onChange={(e) => setInstitutionContactEmail(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="legal@yourinstitution.com"
                  sx={darkFieldSx}
                />
              </Stack>
            </Box>

            <Alert
              severity="warning"
              sx={{
                bgcolor: alpha(athenaColors.periwinkle, 0.12),
                border: `1px solid ${alpha(athenaColors.periwinkle, 0.3)}`,
                "& .MuiAlert-icon": { color: athenaColors.periwinkle },
                "& .MuiAlert-message": { color: athenaColors.periwinkle },
              }}
            >
              {t("contracts_wizard.agreement.modules_warning", "Check only the modules relevant to your use case before proceeding.")}
            </Alert>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "space-between" }}>
              <Button onClick={onBack} sx={athenaDarkSecondaryButtonSx}>
                {t("contracts_wizard.agreement.back", "Back")}
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={generating || !documentId || !signerTitle || !institutionAddress || !institutionNif || !institutionDescription}
                startIcon={generating ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={athenaDarkPrimaryButtonSx}
              >
                {t("contracts_wizard.agreement.generate", "Generate Agreement")}
              </Button>
            </Box>
          </>
        )}
      </Stack>
    );
  }

  if (subPhase === "review") {
    return (
      <Stack spacing={2}>
        <Box sx={athenaDarkSectionSx}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: athenaColors.white, mb: 1 }}>
            {t("contracts_wizard.agreement.review_title", "Review your agreement")}
          </Typography>
          {blobUrl ? (
            <Box sx={{ border: `1px solid ${alpha(athenaColors.periwinkle, 0.3)}`, borderRadius: 2, overflow: "hidden", minHeight: 400 }}>
              <object data={blobUrl} type="application/pdf" width="100%" height="500px" />
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: alpha(athenaColors.white, 0.8) }}>
              {t("contracts_wizard.agreement.no_preview", "PDF not available. Download to view.")}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPdf}
              disabled={!blobUrl}
              sx={athenaDarkSecondaryButtonSx}
            >
              {t("contracts_wizard.agreement.download", "Download PDF")}
            </Button>
            <Button onClick={handleContinueToSign} sx={athenaDarkPrimaryButtonSx}>
              {t("contracts_wizard.agreement.continue_to_sign", "Continue to Sign")}
            </Button>
          </Stack>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <Button onClick={() => setSubPhase("configure")} sx={athenaDarkSecondaryButtonSx}>
            {t("contracts_wizard.agreement.back", "Back")}
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{
            bgcolor: alpha(theme.palette.error.main, 0.12),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            "& .MuiAlert-icon": { color: theme.palette.error.light },
            "& .MuiAlert-message": { color: alpha(athenaColors.white, 0.95) },
          }}
        >
          {error}
        </Alert>
      )}
      <Box sx={athenaDarkSectionSx}>
        <Tabs
          value={signTab}
          onChange={(_, v) => setSignTab(v)}
          sx={{
            mb: 2,
            minHeight: 40,
            "& .MuiTab-root": { color: alpha(athenaColors.periwinkle, 0.7), textTransform: "none", fontWeight: 700 },
            "& .Mui-selected": { color: `${athenaColors.periwinkle} !important` },
            "& .MuiTabs-indicator": { backgroundColor: athenaColors.periwinkle },
          }}
        >
          <Tab label={t("contracts_wizard.agreement.sign_digitally", "Sign Digitally")} />
          <Tab label={t("contracts_wizard.agreement.upload_signed", "Upload Signed Copy")} />
        </Tabs>

        {signTab === 0 && (
          <>
            {!otpSent ? (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: athenaColors.white, mb: 1 }}>
                  {t("contracts_wizard.agreement.electronic_signature", "Electronic Signature")}
                </Typography>
                <Typography variant="body2" sx={{ color: alpha(athenaColors.white, 0.8), mb: 2 }}>
                  {t("contracts_wizard.agreement.type_name_instruction", 'Type your full name exactly as shown below to confirm your intent to sign.')}
                  {" "}<strong style={{ color: athenaColors.periwinkle }}>{signerName}</strong>
                </Typography>
                <TextField
                  label={t("contracts_wizard.agreement.typed_signature_label", "Type your full name to sign")}
                  value={typedSignature}
                  onChange={(e) => setTypedSignature(e.target.value)}
                  fullWidth
                  size="small"
                  error={typedSignature.length > 0 && !signatureMatches}
                  helperText={
                    typedSignature.length > 0 && !signatureMatches
                      ? t("contracts_wizard.agreement.name_must_match", "Must match your account name exactly")
                      : " "
                  }
                  sx={darkFieldSx}
                />
                <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirmRead}
                        onChange={(_, c) => setConfirmRead(c)}
                        sx={{ color: alpha(athenaColors.white, 0.6), "&.Mui-checked": { color: athenaColors.periwinkle } }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: athenaColors.white }}>
                        {t("contracts_wizard.agreement.confirm_read", "I have read and understand the agreement.")}
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirmAuthorized}
                        onChange={(_, c) => setConfirmAuthorized(c)}
                        sx={{ color: alpha(athenaColors.white, 0.6), "&.Mui-checked": { color: athenaColors.periwinkle } }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: athenaColors.white }}>
                        {t("contracts_wizard.agreement.confirm_authorized", "I am authorized to sign on behalf of my institution.")}
                      </Typography>
                    }
                  />
                </Stack>
                <Button
                  onClick={handleRequestOtp}
                  disabled={!signatureMatches || !confirmRead || !confirmAuthorized || sendingOtp}
                  startIcon={sendingOtp ? <CircularProgress size={16} color="inherit" /> : undefined}
                  sx={{ ...athenaDarkPrimaryButtonSx, mt: 2 }}
                >
                  {sendingOtp
                    ? t("contracts_wizard.agreement.sending_code", "Sending code...")
                    : t("contracts_wizard.agreement.send_verification_code", "Send Verification Code")}
                </Button>
              </Box>
            ) : (
              <Box>
                <Alert
                  severity="info"
                  sx={{
                    mb: 2,
                    bgcolor: alpha(athenaColors.periwinkle, 0.12),
                    border: `1px solid ${alpha(athenaColors.periwinkle, 0.3)}`,
                    "& .MuiAlert-icon": { color: athenaColors.periwinkle },
                    "& .MuiAlert-message": { color: athenaColors.periwinkle },
                  }}
                >
                  {t("contracts_wizard.agreement.otp_sent_message", "A 6-digit code has been sent to")}
                  {" "}<strong>{emailHint}</strong>.
                  {" "}{t("contracts_wizard.agreement.otp_expires", "It expires in 10 minutes.")}
                </Alert>
                <TextField
                  label={t("contracts_wizard.agreement.verification_code", "Verification code")}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  fullWidth
                  size="small"
                  placeholder="000000"
                  inputProps={{ maxLength: 6 }}
                  sx={darkFieldSx}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                  <Button
                    size="small"
                    onClick={() => { setOtpSent(false); setOtpCode(""); setOtpError(""); }}
                    sx={{ ...athenaDarkSecondaryButtonSx, fontSize: "0.8rem" }}
                  >
                    {t("contracts_wizard.agreement.back", "Back")}
                  </Button>
                  <Button
                    size="small"
                    onClick={handleRequestOtp}
                    disabled={sendingOtp}
                    sx={{ ...athenaDarkSecondaryButtonSx, fontSize: "0.8rem" }}
                  >
                    {sendingOtp
                      ? t("contracts_wizard.agreement.sending", "Sending...")
                      : t("contracts_wizard.agreement.resend_code", "Resend code")}
                  </Button>
                </Box>
                {otpError && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {otpError}
                  </Typography>
                )}
                <Button
                  onClick={handleSignDigitally}
                  disabled={otpCode.length !== 6 || signing}
                  startIcon={signing ? <CircularProgress size={16} color="inherit" /> : undefined}
                  fullWidth
                  sx={{ ...athenaDarkPrimaryButtonSx, mt: 2 }}
                >
                  {signing
                    ? t("contracts_wizard.agreement.signing", "Signing...")
                    : t("contracts_wizard.agreement.verify_and_sign", "Verify & Sign")}
                </Button>
              </Box>
            )}
          </>
        )}

        {signTab === 1 && (
          <>
            <Typography variant="body2" sx={{ color: alpha(athenaColors.white, 0.9), mb: 1 }}>
              {t("contracts_wizard.agreement.upload_hint_qualified",
                "Upload a PDF signed with a qualified electronic signature (e.g. AutoFirma, Adobe Sign, DocuSign). Scanned wet-ink signatures are not accepted — use the platform's electronic signing flow instead.")}
            </Typography>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              style={{ display: "block", marginBottom: 8 }}
            />
            <Button
              onClick={handleUploadSigned}
              disabled={!uploadFile || uploading}
              startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ ...athenaDarkPrimaryButtonSx, mt: 2 }}
            >
              {t("contracts_wizard.agreement.upload", "Upload Signed PDF")}
            </Button>
          </>
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
        <Button onClick={() => setSubPhase("review")} sx={athenaDarkSecondaryButtonSx}>
          {t("contracts_wizard.agreement.back", "Back")}
        </Button>
      </Box>
    </Stack>
  );
}
